import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiUsers,
  FiBarChart2,
  FiRefreshCw,
  FiX,
} from "react-icons/fi";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import {
  getDashboardSummary,
  getBeltDistribution,
  triggerEmployeeSync,
  getOrgCategoryBreakdown,
  getLeaderboard,
} from "../api/client";
import { FiAward, FiAlertTriangle } from "react-icons/fi";
import CombinedScoreCard from "../components/CombinedScoreCard";
import { useAuth } from "../context/AuthContext";

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [beltDistribution, setBeltDistribution] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [summaryRes, beltRes, catRes, lbRes] = await Promise.all([
          getDashboardSummary(),
          getBeltDistribution(),
          getOrgCategoryBreakdown().catch(() => ({ data: { categories: [] } })),
          getLeaderboard(10).catch(() => ({ data: [] })),
        ]);
        const s = summaryRes.data;
        const mappedSummary = {
          totalEmployees: s.total_employees || 0,
          assessed: s.total_assessed || 0,
          notAssessed: s.total_not_assessed || 0,
          averageScore: s.overall_avg_score,
          greenCount: s.green_belt_count || 0,
          yellowCount: s.yellow_belt_count || 0,
          redCount: s.red_belt_count || 0,
          // New: full belt_counts dict keyed by color slug
          beltCounts: s.belt_counts || {},
          departments: (s.departments || []).map((d) => ({
            businessUnit: d.business_unit,
            totalEmployees: d.total_employees,
            assessedCount: d.assessed_count,
            notAssessedCount: d.not_assessed_count,
            averageScore: d.avg_score,
            greenCount: d.green_belt_count || 0,
            yellowCount: d.yellow_belt_count || 0,
            redCount: d.red_belt_count || 0,
          })),
          // New: 11-rollup department comparison (preferred for the bar chart)
          departmentRollups: (s.department_rollups || []).map((r) => ({
            rollup: r.rollup,
            businessUnits: r.business_units || [],
            totalEmployees: r.total_employees,
            assessedCount: r.assessed_count,
            notAssessedCount: r.not_assessed_count,
            averageScore: r.avg_score,
          })),
          hierarchyStats: (s.hierarchy_stats || []).map((h) => ({
            depth: h.depth,
            label: h.label,
            count: h.count,
            assessedCount: h.assessed_count || 0,
            avgScore: h.avg_score,
          })),
        };
        const b = beltRes.data;
        const mappedBelt = {
          // Per-color raw counts from the API (green/yellow/red/white/black/…)
          beltCounts: b.belt_counts || {},
          // Total employees actually assessed is the sum of belt counts; the
          // correct not-assessed number comes from a separate field. Never
          // again mix them up.
          notAssessed: b.not_assessed_count || 0,
          total: b.total || 0,
          // Legacy fields retained for any downstream consumer
          green: b.green_count || 0,
          yellow: b.yellow_count || 0,
          red: b.red_count || 0,
        };
        setSummary(mappedSummary);
        setBeltDistribution(mappedBelt);
        setCategoryBreakdown(catRes.data?.categories || []);
        setLeaderboard(lbRes.data || []);
      } catch (err) {
        setError(err.message || "Failed to load dashboard data");
        console.error("Dashboard error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "var(--bg-primary)",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <FiRefreshCw
            size={40}
            style={{ color: "var(--color-highlight)" }}
          />
        </motion.div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "var(--bg-primary)",
        color: "var(--text-primary)",
        minHeight: "100vh",
        padding: "24px",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: "32px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1
              style={{
                margin: "0 0 8px 0",
                fontSize: "28px",
                fontWeight: 800,
                letterSpacing: "-0.5px",
              }}
            >
              AI Capability Dashboard
            </h1>
            <p
              style={{
                margin: "0",
                fontSize: "14px",
                color: "var(--text-secondary)",
                fontWeight: 500,
              }}
            >
              Organization-wide assessment overview
            </p>
          </div>
          {user?.is_admin && <SyncButton />}
        </div>
      </motion.div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            border: "1px solid #ef4444",
            borderRadius: "var(--radius-md)",
            padding: "12px 16px",
            marginBottom: "24px",
            color: "#fca5a5",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: "none",
              border: "none",
              color: "#fca5a5",
              cursor: "pointer",
              padding: "4px",
            }}
          >
            <FiX size={16} />
          </button>
        </motion.div>
      )}

      {/* Row 1: Summary Cards */}
      {summary && <SummaryCards data={summary} />}

      {/* Row 1b: Combined Score (LMS + Assessment). Only renders when admin
           or there's at least one employee in scope; the widget itself
           handles the empty/error states. */}
      <CombinedScoreCard />

      {/* Row 2: Belt Distribution & Assessment Coverage */}
      {summary && beltDistribution && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            marginBottom: "32px",
          }}
        >
          <BeltDistributionChart data={beltDistribution} summary={summary} />
          <AssessmentCoverageChart summary={summary} />
        </div>
      )}

      {/* Row 3: Department Comparison (11-bucket rollup of 42 BUs) */}
      {summary && summary.departmentRollups?.length > 0 && (
        <DepartmentComparisonChart rollups={summary.departmentRollups} />
      )}

      {/* Row 3b: Leaderboard (top 10 scorers with violation flags) */}
      {leaderboard.length > 0 && <Leaderboard entries={leaderboard} />}

      {/* Row 4: Category-wise Performance */}
      {categoryBreakdown.length > 0 && (
        <CategoryBreakdownPanel categories={categoryBreakdown} />
      )}

      {/* Row 5: Hierarchy Breakdown */}
      {summary && summary.hierarchyStats.length > 0 && (
        <HierarchyBreakdownTable hierarchyStats={summary.hierarchyStats} />
      )}
    </div>
  );
};

const CategoryBreakdownPanel = ({ categories }) => {
  const getColor = (pct) => {
    if (pct == null) return "#6b7280";
    if (pct >= 70) return "#10b981";
    if (pct >= 40) return "#eab308";
    return "#ef4444";
  };

  const sorted = [...categories].sort((a, b) => b.score_percentage - a.score_percentage);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: "var(--bg-card)",
        borderRadius: "var(--radius-md)",
        padding: "24px",
        border: "1px solid var(--border-subtle)",
        marginBottom: "32px",
      }}
    >
      <h2
        style={{
          margin: "0 0 6px 0",
          fontSize: "20px",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <FiBarChart2 size={22} style={{ color: "var(--color-highlight)" }} />
        Category-wise Performance
      </h2>
      <p style={{ margin: "0 0 20px 0", fontSize: "13px", color: "var(--text-secondary)" }}>
        Organization-wide accuracy across every answered question, grouped by topic.
      </p>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <th style={{ padding: "12px", textAlign: "left", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700, color: "var(--text-secondary)" }}>Category</th>
              <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700, color: "var(--text-secondary)" }}>Sessions</th>
              <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700, color: "var(--text-secondary)" }}>Correct / Total</th>
              <th style={{ padding: "12px", textAlign: "left", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700, color: "var(--text-secondary)" }}>Accuracy</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => {
              const pct = c.score_percentage || 0;
              const color = getColor(pct);
              return (
                <tr key={c.category} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                  <td style={{ padding: "12px", fontWeight: 600, color: "var(--text-primary)" }}>{c.category}</td>
                  <td style={{ padding: "12px", textAlign: "center", color: "var(--text-secondary)" }}>{c.sessions_count ?? "—"}</td>
                  <td style={{ padding: "12px", textAlign: "center", color: "var(--text-secondary)" }}>{c.correct} / {c.total}</td>
                  <td style={{ padding: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{ flex: 1, height: "8px", backgroundColor: "rgba(0,0,0,0.1)", borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, backgroundColor: color, transition: "width 0.3s ease" }} />
                      </div>
                      <span style={{ fontSize: "13px", fontWeight: 700, color, minWidth: "48px", textAlign: "right" }}>
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

// Summary Cards Component - Professional with left border accent
const SummaryCards = ({ data }) => {
  const assessmentPercentage =
    data.totalEmployees > 0
      ? ((data.assessed / data.totalEmployees) * 100).toFixed(1)
      : 0;
  const notAssessedPercentage =
    data.totalEmployees > 0
      ? ((data.notAssessed / data.totalEmployees) * 100).toFixed(1)
      : 0;

  const cards = [
    {
      label: "Total Employees",
      value: data.totalEmployees.toLocaleString(),
      percentage: null,
      borderColor: "#3b82f6",
    },
    {
      label: "Assessed",
      value: data.assessed.toLocaleString(),
      percentage: `${assessmentPercentage}% of total`,
      borderColor: "#8b5cf6",
    },
    {
      label: "Average Score",
      value:
        data.averageScore != null
          ? `${data.averageScore.toFixed(1)}%`
          : "N/A",
      percentage: "Organization-wide",
      borderColor: "#10b981",
    },
    {
      label: "Not Assessed",
      value: data.notAssessed.toLocaleString(),
      percentage: `${notAssessedPercentage}% of total`,
      borderColor: "#f59e0b",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "16px",
        marginBottom: "32px",
      }}
    >
      {cards.map((card, idx) => (
        <motion.div
          key={idx}
          whileHover={{ y: -2 }}
          style={{
            backgroundColor: "var(--bg-card)",
            borderRadius: "var(--radius-md)",
            padding: "20px",
            border: "1px solid var(--border-subtle)",
            borderLeft: `4px solid ${card.borderColor}`,
            cursor: "default",
          }}
        >
          <h3
            style={{
              margin: "0 0 12px 0",
              fontSize: "12px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontWeight: 600,
              color: "var(--text-secondary)",
            }}
          >
            {card.label}
          </h3>
          <p
            style={{
              margin: "0 0 6px 0",
              fontSize: "32px",
              fontWeight: 800,
              color: "var(--text-primary)",
            }}
          >
            {card.value}
          </p>
          {card.percentage && (
            <p
              style={{
                margin: "0",
                fontSize: "12px",
                color: "var(--text-secondary)",
                fontWeight: 500,
              }}
            >
              {card.percentage}
            </p>
          )}
        </motion.div>
      ))}
    </motion.div>
  );
};

// Belt Distribution Pie Chart
// Renders one slice per belt color in the BeltConfig table + one "Not
// Assessed" slice driven by the separate `not_assessed_count` field. This
// way Black Belt / White Belt / any future belt colors get their own slice
// instead of being dumped into "Not Assessed" (the bug that made the card
// report 748 unassessed when the real number was 290).
const BELT_COLOR_META = {
  black:  { label: "Black Belt",  fill: "#111827" },
  green:  { label: "Green Belt",  fill: "#10b981" },
  yellow: { label: "Yellow Belt", fill: "#eab308" },
  red:    { label: "Red Belt",    fill: "#ef4444" },
  white:  { label: "White Belt",  fill: "#94a3b8" },
};
const beltMeta = (color) =>
  BELT_COLOR_META[color] || {
    label: (color || "Other").replace(/\b\w/g, (c) => c.toUpperCase()) + " Belt",
    fill: "#6366f1",
  };

const BeltDistributionChart = ({ data, summary }) => {
  const counts = data.beltCounts || {};
  const notAssessed = data.notAssessed || 0;
  const total = data.total || summary.totalEmployees || 1;

  // One slice per belt color present, then not-assessed. Stable order by
  // canonical rank (black > green > yellow > white > red > …) so reloads
  // don't shuffle the donut.
  const order = ["black", "green", "yellow", "white", "red"];
  const colorKeys = Object.keys(counts).sort((a, b) => {
    const ia = order.indexOf(a); const ib = order.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  const chartData = [
    ...colorKeys.map((k) => ({
      name: beltMeta(k).label,
      value: counts[k] || 0,
      fill: beltMeta(k).fill,
    })),
    { name: "Not Assessed", value: notAssessed, fill: "#4b5563" },
  ].filter((item) => item.value > 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0];
      const percent = ((value / total) * 100).toFixed(1);
      return (
        <div
          style={{
            backgroundColor: "#1f2937",
            border: "1px solid #374151",
            borderRadius: "6px",
            padding: "8px 12px",
            color: "#f3f4f6",
            fontSize: "12px",
          }}
        >
          <p style={{ margin: "0" }}>
            <strong>{name}</strong>
          </p>
          <p style={{ margin: "4px 0 0 0" }}>
            {value} ({percent}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: "var(--bg-card)",
        borderRadius: "var(--radius-md)",
        padding: "24px",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <h2
        style={{
          margin: "0 0 20px 0",
          fontSize: "20px",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <FiBarChart2
          size={22}
          style={{ color: "var(--color-highlight)" }}
        />
        Belt Distribution
      </h2>

      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={80}
            outerRadius={120}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>

      <div
        style={{
          textAlign: "center",
          marginTop: "16px",
          paddingTop: "16px",
          borderTop: "1px solid var(--border-subtle)",
        }}
      >
        <p
          style={{
            margin: "0 0 8px 0",
            fontSize: "12px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontWeight: 600,
            color: "var(--text-secondary)",
          }}
        >
          Total Assessed
        </p>
        <p
          style={{
            margin: "0",
            fontSize: "28px",
            fontWeight: 800,
            color: "var(--text-primary)",
          }}
        >
          {summary.assessed}
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "12px",
          marginTop: "16px",
        }}
      >
        {[
          ...colorKeys.map((k) => ({
            label: beltMeta(k).label,
            value: counts[k] || 0,
            color: beltMeta(k).fill,
          })),
          {
            label: "Not Assessed",
            value: notAssessed,
            color: "#4b5563",
          },
        ].map((item, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px",
              backgroundColor: "rgba(0,0,0,0.1)",
              borderRadius: "var(--radius-sm)",
            }}
          >
            <div
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "2px",
                backgroundColor: item.color,
                flexShrink: 0,
              }}
            />
            <div
              style={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <p
                style={{
                  margin: "0",
                  fontSize: "12px",
                  color: "var(--text-secondary)",
                }}
              >
                {item.label}
              </p>
            </div>
            <p
              style={{
                margin: "0",
                fontSize: "14px",
                fontWeight: 700,
                color: "var(--text-primary)",
              }}
            >
              {item.value}
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// Assessment Coverage Radial Chart
const AssessmentCoverageChart = ({ summary }) => {
  const assessmentPercentage =
    summary.totalEmployees > 0
      ? (summary.assessed / summary.totalEmployees) * 100
      : 0;
  const notAssessedPercentage = 100 - assessmentPercentage;

  const chartData = [
    {
      name: "Assessed",
      value: assessmentPercentage,
      fill: "#10b981",
    },
    {
      name: "Not Assessed",
      value: notAssessedPercentage,
      fill: "#4b5563",
    },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0];
      return (
        <div
          style={{
            backgroundColor: "#1f2937",
            border: "1px solid #374151",
            borderRadius: "6px",
            padding: "8px 12px",
            color: "#f3f4f6",
            fontSize: "12px",
          }}
        >
          <p style={{ margin: "0" }}>
            <strong>{name}</strong>
          </p>
          <p style={{ margin: "4px 0 0 0" }}>{value.toFixed(1)}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: "var(--bg-card)",
        borderRadius: "var(--radius-md)",
        padding: "24px",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <h2
        style={{
          margin: "0 0 20px 0",
          fontSize: "20px",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <FiUsers
          size={22}
          style={{ color: "var(--color-highlight)" }}
        />
        Assessment Coverage
      </h2>

      <ResponsiveContainer width="100%" height={300}>
        <RadialBarChart
          cx="50%"
          cy="50%"
          innerRadius="30%"
          outerRadius="90%"
          data={chartData}
          startAngle={180}
          endAngle={0}
        >
          <PolarAngleAxis
            type="number"
            domain={[0, 100]}
            tick={false}
            angleAxisId={0}
          />
          <RadialBar
            background
            dataKey="value"
            cornerRadius={10}
            label={false}
          />
          <Tooltip content={<CustomTooltip />} />
        </RadialBarChart>
      </ResponsiveContainer>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          marginTop: "16px",
          paddingTop: "16px",
          borderTop: "1px solid var(--border-subtle)",
        }}
      >
        <div
          style={{
            textAlign: "center",
          }}
        >
          <p
            style={{
              margin: "0 0 8px 0",
              fontSize: "12px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontWeight: 600,
              color: "var(--text-secondary)",
            }}
          >
            Assessed
          </p>
          <p
            style={{
              margin: "0",
              fontSize: "28px",
              fontWeight: 800,
              color: "#10b981",
            }}
          >
            {assessmentPercentage.toFixed(1)}%
          </p>
          <p
            style={{
              margin: "4px 0 0 0",
              fontSize: "12px",
              color: "var(--text-secondary)",
            }}
          >
            {summary.assessed} of {summary.totalEmployees}
          </p>
        </div>
        <div
          style={{
            textAlign: "center",
          }}
        >
          <p
            style={{
              margin: "0 0 8px 0",
              fontSize: "12px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontWeight: 600,
              color: "var(--text-secondary)",
            }}
          >
            Not Assessed
          </p>
          <p
            style={{
              margin: "0",
              fontSize: "28px",
              fontWeight: 800,
              color: "#4b5563",
            }}
          >
            {notAssessedPercentage.toFixed(1)}%
          </p>
          <p
            style={{
              margin: "4px 0 0 0",
              fontSize: "12px",
              color: "var(--text-secondary)",
            }}
          >
            {summary.notAssessed} of {summary.totalEmployees}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Department Comparison Bar Chart
// Department Comparison Chart — 11-bucket functional rollup of the 42 raw BUs.
// Green = assessed, Red = not assessed. Stacked horizontal bars so the length
// of each row shows total department headcount at a glance.
const DepartmentComparisonChart = ({ rollups }) => {
  // Preserve backend order (defined in DEPARTMENT_ROLLUP dict). Sorting here
  // would override product's chosen ordering.
  const data = rollups.map((r) => ({
    ...r,
    coverage:
      r.totalEmployees > 0
        ? Math.round((r.assessedCount / r.totalEmployees) * 100)
        : 0,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#1f2937",
            border: "1px solid #374151",
            borderRadius: "6px",
            padding: "10px 14px",
            color: "#f3f4f6",
            fontSize: "12px",
            maxWidth: 320,
          }}
        >
          <p style={{ margin: "0 0 6px 0", fontSize: 13 }}>
            <strong>{d.rollup}</strong>
          </p>
          <p style={{ margin: "2px 0" }}>
            Total:&nbsp;<strong>{d.totalEmployees}</strong>
          </p>
          <p style={{ margin: "2px 0", color: "#10b981" }}>
            Assessed:&nbsp;<strong>{d.assessedCount}</strong>&nbsp;({d.coverage}%)
          </p>
          <p style={{ margin: "2px 0", color: "#ef4444" }}>
            Not Assessed:&nbsp;<strong>{d.notAssessedCount}</strong>
          </p>
          {d.averageScore != null && (
            <p style={{ margin: "6px 0 0 0" }}>
              Avg Score:&nbsp;<strong>{d.averageScore.toFixed(1)}%</strong>
            </p>
          )}
          {d.businessUnits?.length > 0 && (
            <p style={{ margin: "6px 0 0 0", fontSize: 11, color: "#94a3b8" }}>
              Units: {d.businessUnits.slice(0, 5).join(", ")}
              {d.businessUnits.length > 5
                ? ` +${d.businessUnits.length - 5} more`
                : ""}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: "var(--bg-card)",
        borderRadius: "var(--radius-md)",
        padding: "24px",
        border: "1px solid var(--border-subtle)",
        marginBottom: "32px",
      }}
    >
      <h2
        style={{
          margin: "0 0 4px 0",
          fontSize: "20px",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <FiBarChart2
          size={22}
          style={{ color: "var(--color-highlight)" }}
        />
        Department Comparison — Assessed vs Not Assessed
      </h2>
      <p
        style={{
          margin: "0 0 20px 0",
          fontSize: 12,
          color: "var(--text-secondary)",
        }}
      >
        42 business units rolled into 11 functional departments
      </p>

      <ResponsiveContainer width="100%" height={Math.max(320, data.length * 42)}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
          <XAxis type="number" stroke="var(--text-secondary)" />
          <YAxis
            dataKey="rollup"
            type="category"
            width={220}
            stroke="var(--text-secondary)"
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
          <Legend />
          <Bar
            dataKey="assessedCount"
            stackId="dept"
            fill="#10b981"
            name="Assessed"
          />
          <Bar
            dataKey="notAssessedCount"
            stackId="dept"
            fill="#ef4444"
            name="Not Assessed"
            radius={[0, 8, 8, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </motion.div>
  );
};


// Leaderboard — top N scorers with belt + violation flags. Major violations
// get a red triangle, minor get a yellow triangle. Tap-target is the row
// itself (no click action today; future: drill into session).
const Leaderboard = ({ entries }) => {
  const medal = (rank) =>
    rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`;

  const beltPill = (color, name) => {
    const meta = BELT_COLOR_META[color] || {
      label: name || "—",
      fill: "#6b7280",
    };
    return (
      <span
        style={{
          background: meta.fill,
          color: "#fff",
          padding: "2px 10px",
          borderRadius: 10,
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        {name || meta.label}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: "var(--bg-card)",
        borderRadius: "var(--radius-md)",
        padding: "24px",
        border: "1px solid var(--border-subtle)",
        marginBottom: "32px",
      }}
    >
      <h2
        style={{
          margin: "0 0 4px 0",
          fontSize: "20px",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <FiAward size={22} style={{ color: "var(--color-highlight)" }} />
        Leaderboard — Top Scorers
      </h2>
      <p
        style={{
          margin: "0 0 16px 0",
          fontSize: 12,
          color: "var(--text-secondary)",
        }}
      >
        Best score across all attempts. Violation flags surface suspicious
        high scores — a red triangle = major violation, yellow = minor.
      </p>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--text-secondary)", fontSize: 12 }}>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>#</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Name</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Department</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Score</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>%</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Belt</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Flags</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const hasMajor = e.major_violations > 0;
              const hasMinor = e.minor_violations > 0;
              return (
                <tr
                  key={e.best_session_id}
                  style={{
                    borderTop: "1px solid var(--border-subtle)",
                    background: hasMajor
                      ? "rgba(239, 68, 68, 0.06)"
                      : "transparent",
                  }}
                >
                  <td style={{ padding: "10px", fontWeight: 600 }}>
                    {medal(e.rank)}
                  </td>
                  <td style={{ padding: "10px" }}>
                    <div style={{ fontWeight: 600 }}>{e.name || "—"}</div>
                    <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                      {e.designation || ""}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "10px",
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 12,
                      color: "var(--text-secondary)",
                    }}
                    title={e.business_unit || ""}
                  >
                    {e.business_unit || "—"}
                  </td>
                  <td style={{ padding: "10px", fontFamily: "monospace" }}>
                    {e.score}/{e.total_questions}
                  </td>
                  <td style={{ padding: "10px", fontWeight: 700 }}>
                    {e.score_percentage?.toFixed
                      ? e.score_percentage.toFixed(1)
                      : e.score_percentage}
                    %
                  </td>
                  <td style={{ padding: "10px" }}>
                    {beltPill(e.belt_color, e.belt)}
                  </td>
                  <td style={{ padding: "10px" }}>
                    {hasMajor && (
                      <span
                        title={`${e.major_violations} major violation(s)`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                          background: "rgba(239, 68, 68, 0.15)",
                          color: "#ef4444",
                          padding: "2px 8px",
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 600,
                          marginRight: 6,
                        }}
                      >
                        <FiAlertTriangle size={12} />
                        {e.major_violations} Major
                      </span>
                    )}
                    {hasMinor && (
                      <span
                        title={`${e.minor_violations} minor violation(s)`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                          background: "rgba(234, 179, 8, 0.15)",
                          color: "#eab308",
                          padding: "2px 8px",
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        <FiAlertTriangle size={12} />
                        {e.minor_violations} Minor
                      </span>
                    )}
                    {!hasMajor && !hasMinor && (
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        Clean
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

// Hierarchy Breakdown Table with Progress Bars
const HierarchyBreakdownTable = ({ hierarchyStats }) => {
  const maxAssessedCount = Math.max(
    ...hierarchyStats.map((h) => h.assessedCount || 0),
    1
  );

  const getScoreColor = (score) => {
    if (score == null) return "#6b7280";
    if (score >= 70) return "#10b981";
    if (score >= 40) return "#eab308";
    return "#ef4444";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: "var(--bg-card)",
        borderRadius: "var(--radius-md)",
        padding: "24px",
        border: "1px solid var(--border-subtle)",
        marginBottom: "32px",
      }}
    >
      <h2
        style={{
          margin: "0 0 20px 0",
          fontSize: "20px",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <FiBarChart2
          size={22}
          style={{ color: "var(--color-highlight)" }}
        />
        Hierarchy Breakdown
      </h2>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontWeight: 700,
                  color: "var(--text-secondary)",
                }}
              >
                Hierarchy Level
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontWeight: 700,
                  color: "var(--text-secondary)",
                }}
              >
                Total
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontWeight: 700,
                  color: "var(--text-secondary)",
                }}
              >
                Assessed
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontWeight: 700,
                  color: "var(--text-secondary)",
                }}
              >
                Progress
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontWeight: 700,
                  color: "var(--text-secondary)",
                }}
              >
                Avg Score
              </th>
            </tr>
          </thead>
          <tbody>
            {hierarchyStats.map((h, idx) => {
              const assessmentPercentage =
                h.count > 0 ? ((h.assessedCount || 0) / h.count) * 100 : 0;
              return (
                <tr
                  key={idx}
                  style={{
                    borderBottom: "1px solid var(--border-subtle)",
                  }}
                >
                  <td
                    style={{
                      padding: "12px",
                      fontWeight: 600,
                      color: "var(--text-primary)",
                    }}
                  >
                    {h.label}
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {h.count}
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontWeight: 600,
                      color: "var(--text-primary)",
                    }}
                  >
                    {h.assessedCount || 0}
                  </td>
                  <td
                    style={{
                      padding: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: "8px",
                          backgroundColor: "rgba(0,0,0,0.1)",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${assessmentPercentage}%`,
                            backgroundColor: "#8b5cf6",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          minWidth: "35px",
                          textAlign: "right",
                        }}
                      >
                        {assessmentPercentage.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontWeight: 700,
                      color: getScoreColor(h.avgScore),
                    }}
                  >
                    {h.avgScore != null
                      ? `${h.avgScore.toFixed(1)}%`
                      : "N/A"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

// Sync Button for admins
const SyncButton = () => {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);
    try {
      const res = await triggerEmployeeSync();
      setResult(res.data);
    } catch (err) {
      setResult({
        status: "error",
        message: err.response?.data?.detail || "Sync failed",
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {result && (
        <span
          style={{
            fontSize: "12px",
            color: result.status === "success" ? "#10b981" : "#ef4444",
            maxWidth: "250px",
            textAlign: "right",
          }}
        >
          {result.status === "success"
            ? `Synced: ${result.created || 0} new, ${result.updated || 0} updated`
            : result.message || "Error"}
        </span>
      )}
      <button
        disabled
        title="Sync is temporarily disabled"
        style={{
          padding: "8px 16px",
          fontSize: "13px",
          fontWeight: 500,
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-subtle)",
          background: "var(--bg-card)",
          color: "var(--text-secondary)",
          cursor: "not-allowed",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          transition: "all 0.15s",
          opacity: 0.5,
        }}
      >
        <FiRefreshCw size={14} />
        Sync Employees
      </button>
    </div>
  );
};

export default Dashboard;
